/**
 * The pack photos.
 *
 * PackCam writes a photo per order at the tablet; this is the proof of what went in the
 * box. The thumbnails stream from `GET /orders/:id/photos/:uuid`, which checks that the
 * uuid belongs to this order before it becomes a path, so a uuid from another order is a
 * 404 rather than someone else's parcel.
 *
 * The lightbox is a real dialog: Escape closes it, focus is trapped while it is open, and
 * the full size image is fetched only when one is opened.
 */
import { useEffect, useState } from 'react';
import type { DrawerPhoto } from '@elutide/shared';
import { Button } from '@/components';
import { fullTimestamp } from '@/lib/format';
import { packPhotoUrl } from '../api';

export function PhotoStrip({ orderId, photos }: { orderId: number; photos: readonly DrawerPhoto[] }) {
  const [open, setOpen] = useState<DrawerPhoto | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(null);
      }
    };
    // Capture phase. The Drawer listens on the document too and registered first, so a
    // bubble-phase listener here runs second and Escape closes the whole drawer along with
    // this. Capture on the document runs before any bubble listener on it.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  if (photos.length === 0) {
    return (
      <div className="o-kv">
        <span className="o-kv__key">Photos</span>
        <span className="o-kv__value ord-sub">none recorded</span>
      </div>
    );
  }

  return (
    <div className="o-kv">
      <span className="o-kv__key">Photos</span>
      <span className="o-kv__value">
        <span className="ord-photos">
          {photos.map((photo) => (
            <button
              key={photo.uuid}
              type="button"
              className="ord-photo"
              onClick={() => setOpen(photo)}
              title={photo.captured_at ? fullTimestamp(photo.captured_at) : 'Pack photo'}
            >
              <img
                src={packPhotoUrl(orderId, photo.uuid, 'thumb')}
                alt={`Pack photo taken ${photo.captured_at ? fullTimestamp(photo.captured_at) : 'at packing'}`}
                loading="lazy"
              />
            </button>
          ))}
        </span>
      </span>

      {open ? (
        <div className="ord-lightbox" role="dialog" aria-modal="true" aria-label="Pack photo">
          <div className="ord-lightbox__bar">
            <span className="ord-sub">
              {open.captured_at ? fullTimestamp(open.captured_at) : 'Pack photo'}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
              Close
            </Button>
          </div>
          <img
            className="ord-lightbox__image"
            src={packPhotoUrl(orderId, open.uuid, 'orig')}
            alt="Pack photo, full size"
          />
        </div>
      ) : null}
    </div>
  );
}
